import styled from '@emotion/styled'
import {
  ACTION_BUTTON_TYPE_EDIT,
  ButtonAction,
  Chip,
  ChipIconInfo,
  ChipIconWrapper,
  Flex,
  FlexItem,
  Grid,
  GridCell,
  Panel,
  SPACING_24,
  SpacingVertical,
  Switcher,
  Text,
  TEXT_VARIANT_BOLD,
  Title,
  TITLE_VARIANT_M
} from 'mailup-web-components'
import { editCallback } from 'Pages/Workflow/recoil/callbacks/callbacks'
import { filterDetailsCallback } from 'Pages/Workflow/recoil/callbacks/step5'
import {
  workflowSettingsEmptyState,
  workflowSettingsState
} from 'Pages/Workflow/recoil/state/step5'

import { useIntl } from 'react-intl'
import { useRecoilCallback, useRecoilValue } from 'recoil'

const FilterSwitcher = styled(Switcher)`
  .e-switcher__option__label {
    padding: 0 8px;

    span {
      padding: 0;
    }
  }
`

const BoxOptionalSettings = () => {
  const intl = useIntl()

  const {
    includeGroups,
    excludeGroups,
    applyFilters,
    applyFiltersRule,
    startHour,
    blackoutStartHour,
    blackoutEndHour,
    tz,
    startDate,
    endDate,
    freqBlackoutDays,
    freqBlackoutHours,
    notInWeekends,
    hasFreqSafeGuard,
    frequencySafeGuard
  } = useRecoilValue(workflowSettingsState)

  const emptySettings = useRecoilValue(workflowSettingsEmptyState)

  const edit = useRecoilCallback(editCallback, [])
  const filterDetails = useRecoilCallback(filterDetailsCallback, [])

  const handleEdit = () => {
    edit(4)
  }

  const handleClickFilter = (id) => {
    filterDetails(id)
  }

  const filterAndOptions = [
    {
      label: intl.formatMessage({ id: 'Load.SearchRecipients.filterAll' }),
      value: 'AND'
    }
  ]

  const filterOrOptions = [
    {
      label: intl.formatMessage({ id: 'Load.SearchRecipients.filterOne' }),
      value: 'OR'
    }
  ]

  return (
    <Panel>
      <Flex justifyContent='space-between'>
        <Title variant={TITLE_VARIANT_M}>
          {intl.formatMessage({
            id: 'Load.AutomationWorkflow.OptionalSettingsTitle'
          })}
        </Title>
        <ButtonAction
          type={ACTION_BUTTON_TYPE_EDIT}
          data-mu-test='edit'
          onClick={handleEdit}
        />
      </Flex>

      <SpacingVertical variant={SPACING_24} />

      {emptySettings ? (
        <Text>
          {intl.formatMessage({
            id: 'Load.AutomationWorkflow.OptionalSettingsEmpty'
          })}
        </Text>
      ) : (
        <Flex flexDirection='column' rowGap='16px'>
          {includeGroups.length > 0 && (
            <Grid cols={12}>
              <GridCell span={3}>
                <FlexItem margin='8px 0 0 0'>
                  <Text variant={TEXT_VARIANT_BOLD}>
                    {intl.formatMessage({
                      id: 'Load.SearchRecipients.IncludeGroups'
                    })}
                  </Text>
                </FlexItem>
              </GridCell>
              <GridCell span={9}>
                <Flex flexWrap='wrap' gap='8px'>
                  {includeGroups.map(({ value, label }) => (
                    <FlexItem key={value}>
                      <Chip size='lg' key={value} label={label} />
                    </FlexItem>
                  ))}
                </Flex>
              </GridCell>
            </Grid>
          )}

          {excludeGroups.length > 0 && (
            <Grid cols={12}>
              <GridCell span={3}>
                <FlexItem margin='8px 0 0 0'>
                  <Text variant={TEXT_VARIANT_BOLD}>
                    {intl.formatMessage({
                      id: 'Load.SearchRecipients.ExcludeGroups'
                    })}
                  </Text>
                </FlexItem>
              </GridCell>
              <GridCell span={9}>
                <Flex flexWrap='wrap' gap='8px'>
                  {excludeGroups.map(({ value, label }) => (
                    <FlexItem key={value}>
                      <Chip size='lg' key={value} label={label} />
                    </FlexItem>
                  ))}
                </Flex>
              </GridCell>
            </Grid>
          )}

          {applyFilters.length > 0 && (
            <Grid cols={12}>
              <GridCell span={3}>
                <Flex alignItems='center'>
                  <FlexItem margin='4px 0 0 0' alignSelf='flex-start'>
                    <Text variant={TEXT_VARIANT_BOLD}>
                      {intl.formatMessage({
                        id: 'Load.SearchRecipients.ApplyFilters'
                      })}
                    </Text>
                  </FlexItem>

                  {applyFiltersRule === 'AND' && (
                    <FilterSwitcher value='AND' options={filterAndOptions} />
                  )}
                  {applyFiltersRule === 'OR' && (
                    <FilterSwitcher value='OR' options={filterOrOptions} />
                  )}
                </Flex>
              </GridCell>
              <GridCell span={9}>
                <Flex flexWrap='wrap' gap='8px'>
                  {applyFilters.map(({ value, label }) => (
                    <FlexItem key={value}>
                      <Chip label={label} value={value}>
                        <ChipIconWrapper
                          onClick={() => handleClickFilter(value)}
                          data-mu-test='workflow-step5__apply-filter-details'
                        >
                          <ChipIconInfo />
                        </ChipIconWrapper>
                      </Chip>
                    </FlexItem>
                  ))}
                </Flex>
              </GridCell>
            </Grid>
          )}

          {startHour && (
            <Grid cols={12}>
              <GridCell span={3}>
                <FlexItem margin='8px 0 0 0'>
                  <Text variant={TEXT_VARIANT_BOLD}>
                    {intl.formatMessage({
                      id: 'Load.AutomationWorkflow.SendEmailsAt'
                    })}
                  </Text>
                </FlexItem>
              </GridCell>
              <GridCell span={9}>
                <Chip size='lg' label={startHour} />
              </GridCell>
            </Grid>
          )}

          {blackoutStartHour && blackoutEndHour && (
            <Grid cols={12}>
              <GridCell span={3}>
                <FlexItem margin='8px 0 0 0'>
                  <Text variant={TEXT_VARIANT_BOLD}>
                    {intl.formatMessage({
                      id: 'Load.AutomationWorkflow.OptionalInactiveDays'
                    })}
                  </Text>
                </FlexItem>
              </GridCell>
              <GridCell span={9}>
                <Flex alignItems='center' gap='8px'>
                  <Text htmlElement='span'>
                    {intl.formatMessage({
                      id: 'Load.AutomationWorkflow.OptionalDateFrom'
                    })}
                  </Text>
                  <Chip size='lg' label={blackoutStartHour} />

                  <Text htmlElement='span'>
                    {intl.formatMessage({
                      id: 'Load.AutomationWorkflow.OptionalDateTo'
                    })}
                  </Text>
                  <Chip size='lg' label={blackoutEndHour} />
                </Flex>
              </GridCell>
            </Grid>
          )}

          {tz && (
            <Grid cols={12}>
              <GridCell span={3}>
                <FlexItem margin='8px 0 0 0'>
                  <Text variant={TEXT_VARIANT_BOLD}>
                    {intl.formatMessage({
                      id: 'Load.MailupCommon.setScheduleLblFuso'
                    })}
                  </Text>
                </FlexItem>
              </GridCell>
              <GridCell span={9}>
                <Chip size='lg' label={tz} />
              </GridCell>
            </Grid>
          )}

          {(startDate || endDate) && (
            <Grid cols={12}>
              <GridCell span={3}>
                <FlexItem margin='8px 0 0 0'>
                  <Text variant={TEXT_VARIANT_BOLD}>
                    {intl.formatMessage({
                      id: 'Load.AutomationWorkflow.OptionalActive'
                    })}
                  </Text>
                </FlexItem>
              </GridCell>
              <GridCell span={9}>
                <Flex gap='8px' alignItems='center'>
                  {startDate && (
                    <>
                      <Text htmlElement='span'>
                        {intl.formatMessage({
                          id: 'Load.AutomationWorkflow.OptionalDateFrom'
                        })}
                      </Text>
                      <Chip size='lg' label={startDate} />
                    </>
                  )}

                  {endDate && (
                    <>
                      <Text htmlElement='span'>
                        {intl.formatMessage({
                          id: 'Load.AutomationWorkflow.OptionalDateTo'
                        })}
                      </Text>
                      <Chip size='lg' label={endDate} />
                    </>
                  )}
                </Flex>
              </GridCell>
            </Grid>
          )}

          {hasFreqSafeGuard && (
            <Grid cols={12}>
              <GridCell span={3}>
                <Text variant={TEXT_VARIANT_BOLD}>
                  {intl.formatMessage({
                    id: 'Load.TriggersDetail.TitleFrequenza'
                  })}
                </Text>
              </GridCell>
              <GridCell span={9}>
                {frequencySafeGuard > 0 && (
                  <Flex gap='8px' alignItems='center'>
                    <Text htmlElement='span'>
                      {intl.formatMessage({
                        id: 'Load.TriggersDetail.OnlyOnce'
                      })}
                    </Text>
                    <Chip size='lg' label={frequencySafeGuard} />
                    <Text htmlElement='span'>
                      {intl.formatMessage({
                        id: 'Load.TriggersDetail.Hours'
                      })}
                    </Text>
                  </Flex>
                )}

                {frequencySafeGuard === 0 && (
                  <Text htmlElement='span'>
                    {intl.formatMessage({
                      id: 'Load.TriggersDetail.OnlyOnceOne'
                    })}
                  </Text>
                )}
              </GridCell>
            </Grid>
          )}

          {(freqBlackoutDays.length > 0 || freqBlackoutHours > 0) && (
            <Grid cols={12}>
              <GridCell span={3}>
                <FlexItem margin='8px 0 0 0'>
                  <Text variant={TEXT_VARIANT_BOLD}>
                    {intl.formatMessage({
                      id: 'Load.AutomationWorkflow.OptionalInactiveDate'
                    })}
                  </Text>
                </FlexItem>
              </GridCell>
              <GridCell span={9}>
                <Flex gap='8px' flexWrap='wrap'>
                  {freqBlackoutDays.length > 0 &&
                    freqBlackoutDays.map((blackoutDate) => (
                      <FlexItem key={blackoutDate}>
                        <Chip size='lg' label={blackoutDate} />
                      </FlexItem>
                    ))}
                  {freqBlackoutHours.length > 0 &&
                    freqBlackoutHours.map((freqBlackoutHour) => (
                      <FlexItem key={freqBlackoutHour}>
                        <Chip size='lg' label={freqBlackoutHour} />
                      </FlexItem>
                    ))}
                </Flex>
              </GridCell>
            </Grid>
          )}

          {notInWeekends && (
            <Grid cols={12}>
              <GridCell span={12}>
                <Text variant={TEXT_VARIANT_BOLD}>
                  {intl.formatMessage({
                    id: 'Load.AutomationWorkflow.OptionalInactiveWeekend'
                  })}
                </Text>
              </GridCell>
            </Grid>
          )}
        </Flex>
      )}
    </Panel>
  )
}

export default BoxOptionalSettings
